import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Router, IsActiveMatchOptions } from '@angular/router';
import { translateSignal, TranslocoModule } from '@jsverse/transloco';
import { RouterLinkFactory } from '../../../../core/router/router-link-factory';
import { IconsService } from '@rieaya-core';
import { ImageModule } from 'primeng/image';
import { Laundry } from '../../../../core/interfaces/laundry';
import { environment } from '../../../../../environments/environment';
import {
  ADMIN,
  ADMIN_STAFF,
  LAUNDRY,
  LAUNDRY_MANAGER,
  LAUNDRY_STAFF,
  USER_DATA,
} from '../../../../core/utils/constant';

@Component({
  selector: 'app-menu',
  imports: [CommonModule, RouterModule, ImageModule, TranslocoModule],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.css',
})
export class MenuComponent {
  links = inject(RouterLinkFactory);
  icons = inject(IconsService);
  private router = inject(Router);

  menuItems!: {
    label: string;
    icon: string;
    href?: string;
    expanded?: boolean;
    children?: {
      label: string;
      icon: string;
      href?: string;
      expanded?: boolean;
      children?: { label: string; icon: string; href: string }[];
    }[];
  }[];

  laundry = signal<Laundry>(JSON.parse(localStorage.getItem(LAUNDRY) ?? ''));

  baseUrl = environment.base_url;

  userData = JSON.parse(localStorage.getItem(USER_DATA) || '{}') ?? '';

  LAUNDRY_MANAGER = LAUNDRY_MANAGER;

  isCollectionPointOnlyStaff =
    this.userData.role === LAUNDRY_STAFF &&
    this.userData.onlyCollectionPoint === true;

  constructor() {
    const translations = {
      dashboard: translateSignal('shared/dashboard'),
      branches: translateSignal('shared/branches'),
      collectionPoints: translateSignal('shared/collectionPoints'),
      additionRequests: translateSignal('shared/additionRequests'),
      employees: translateSignal('shared/employees'),
      auditHistory: translateSignal('shared/auditHistory'),
      communication: translateSignal('shared/communication'),
      settings: translateSignal('shared/settings'),
      workingHours: translateSignal('shared/workingHours'),
      orders: translateSignal('shared/orders'),
      deliveryOrders: translateSignal('shared/deliveryOrders'),
      packages: translateSignal('shared/packages'),
      overviewPackages: translateSignal('shared/overviewPackages'),
      packagesSubscriptions: translateSignal('shared/packagesSubscriptions'),
      offersIncreases: translateSignal('shared/offersIncreases'),
      services: translateSignal('shared/services'),
      bills: translateSignal('shared/bills'),
      attachments: translateSignal('shared/attachments'),
      images: translateSignal('shared/gallery'),
      reports: translateSignal('laundries/laundryReports'),
      cashierCollectionReport: translateSignal('cashierCollection/title'),
      laundryDailyOrderStatusReport: translateSignal('dailyOrderStatus/title'),
      creditSalesReport: translateSignal('creditSalesReport/title'),
      correspondences: translateSignal('technicalSupportRequests'),
    };

    effect(() => {
      this.menuItems = [
        ...(this.userData.role === LAUNDRY_MANAGER
          ? [
              {
                label: translations.dashboard(),
                icon: this.icons.chartBar,
                href: this.links.homepage()[0],
              },
              {
                label: translations.branches(),
                icon: this.icons.sitemap,
                href: this.links.branches()[0],
              },
              {
                label: translations.collectionPoints(),
                icon: this.icons.circle,
                href: this.links.collectionPoints()[0],
              },
              {
                label: translations.additionRequests(),
                icon: this.icons.plus,
                href: this.links.additionRequests()[0],
              },
              {
                label: translations.employees(),
                icon: this.icons.briefcase,
                href: this.links.employees()[0],
              },
              {
                label: translations.communication(),
                icon: this.icons.envelope,
                href: this.links.communication()[0],
              },
              {
                label: translations.auditHistory(),
                icon: this.icons.history,
                href: this.links.auditHistory()[0],
              },
              {
                label: translations.services(),
                icon: this.icons.clone,
                href: this.links.services()[0],
              },
            ]
          : []),
        {
          label: translations.orders(),
          icon: this.icons.tags,
          href: this.links.orders()[0],
        },
        ...(this.userData.role === LAUNDRY_MANAGER ||
        (this.userData.role === LAUNDRY_STAFF &&
          !this.isCollectionPointOnlyStaff)
          ? [
              {
                label: translations.deliveryOrders(),
                icon: this.icons.truck,
                href: this.links.deliveryOrders()[0],
              },
            ]
          : []),
        ...(this.userData.role === LAUNDRY_MANAGER ||
        this.userData.role === LAUNDRY_STAFF
          ? [
              {
                label: translations.bills(),
                icon: this.icons.receipt,
                href: this.links.bills()[0],
              },
            ]
          : []),
        ...(this.userData.role === LAUNDRY_MANAGER
          ? [
              {
                label: translations.attachments(),
                icon: this.icons.link,
                href: this.links.attachments()[0],
              },
              {
                label: translations.images(),
                icon: this.icons.images,
                href: this.links.images()[0],
              },
            ]
          : []),

        ...(this.userData.role === LAUNDRY_MANAGER ||
        (this.userData.role === LAUNDRY_STAFF &&
          !this.isCollectionPointOnlyStaff) ||
        this.userData.role === ADMIN ||
        this.userData.role === ADMIN_STAFF
          ? [
              {
                label: translations.reports(),
                icon: this.icons.chartPie,
                href: this.links.reports()[0],
              },
              {
                label: translations.cashierCollectionReport(),
                icon: this.icons.chartBar,
                href: this.links.cashierCollectionReport()[0],
              },
              {
                label: translations.laundryDailyOrderStatusReport(),
                icon: this.icons.chartBar,
                href: this.links.laundryDailyOrderStatusReport()[0],
              },
              {
                label: translations.creditSalesReport(),
                icon: this.icons.chartBar,
                href: this.links.creditSalesReport()[0],
              },
            ]
          : []),
        ...(this.userData.role === LAUNDRY_MANAGER ||
        this.userData.role === LAUNDRY_STAFF ||
        this.userData.role === ADMIN ||
        this.userData.role === ADMIN_STAFF
          ? [
              {
                label: translations.correspondences(),
                icon: this.icons.inbox,
                href: this.links.messages()[0],
              },
            ]
          : []),
        ...(this.userData.role === LAUNDRY_MANAGER
          ? [
              {
                label: translations.packages(),
                icon: this.icons.packages,
                children: [
                  {
                    label: translations.overviewPackages(),
                    icon: this.icons.circleFill,
                    href: this.links.overviewPackages()[0],
                  },
                  {
                    label: translations.packagesSubscriptions(),
                    icon: this.icons.circleFill,
                    href: this.links.packagesSubscriptions()[0],
                  },
                  {
                    label: translations.offersIncreases(),
                    icon: this.icons.circleFill,
                    href: this.links.packagesOffersIncreases()[0],
                  },
                ],
              },
              {
                label: translations.settings(),
                icon: this.icons.cog,
                children: [
                  {
                    label: translations.workingHours(),
                    icon: this.icons.circleFill,
                    href: this.links.settingsWorkingHours()[0],
                  },
                ],
              },
            ]
          : []),
      ];
    });
  }
  isActive(url: string): boolean {
    const options: IsActiveMatchOptions = {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    };
    return this.router.isActive(url, options);
  }
}
